import isNil from 'lodash/isNil'
import {
  GeckoAnnularRing,
  GeckoAssetModel,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoConeCourse,
  GeckoLegacyBoilerRepairRegion,
  GeckoLegacyBoilerWall,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoPlate,
  GeckoPolygonalPlate,
  GeckoShellCourse,
  GeckoShipHull,
} from 'types'

import { createBasicShipHullNominalResolver } from './basicShipHullNominalResolver'
import {
  getMostRecentPlateInBin,
  getMostRecentPolygonalPlateInBin,
} from './repairHelpers'
export type OverrideNominalSpec = {
  xCoordinate: number
  yCoordinate: number
  xUnit: GeckoMeasurementUnit
  yUnit: GeckoMeasurementUnit
  nominalValue: number
  nominalUnit?: GeckoMeasurementUnit
}

export type GetManufactureSpecsForBinFn = (props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  assetModel: GeckoAssetModel
  latestDateToUse: Date | undefined
  overrides?: ReadonlyArray<OverrideNominalSpec>
}) => {
  nominalThickness: GeckoMeasurement | undefined
  installationDate: Date | undefined
}
/**
 * A function to get the manufacture specs (nominal thickness and/or installation date) for a given bin.
 *
 * Note: If you use forecastComponentConfig values in your component's getManufactureSpecsFor<ComponentType> function,
 *       please pull the relevant data from it in this function and pass it in as a prop so that it will be easier to
 *       migrate the values to the RAM.
 *
 * @param props.binBoundsToGetSpecsFor    The bounds of the bin to get the specs for
 * @param props.assetModel                The asset model to get the specs from
 * @param props.targetComponentId         The ID of the component to get the specs for
 */
export const getManufactureSpecsForBin: GetManufactureSpecsForBinFn = (
  props
) => {
  const { binBoundsToGetSpecsFor, assetModel, latestDateToUse, overrides } =
    props
  const targetComponentId = binBoundsToGetSpecsFor.componentID

  if (overrides && overrides.length > 0) {
    const CENTER_TOLERANCE = 1e-6
    const match = overrides.find((ovr) => {
      const xStart = binBoundsToGetSpecsFor.x.start.convertTo(ovr.xUnit).num
      const xEnd = binBoundsToGetSpecsFor.x.end.convertTo(ovr.xUnit).num
      const yStart = binBoundsToGetSpecsFor.y.start.convertTo(ovr.yUnit).num
      const yEnd = binBoundsToGetSpecsFor.y.end.convertTo(ovr.yUnit).num
      const xCenterConverted = (xStart + xEnd) / 2
      const yCenterConverted = (yStart + yEnd) / 2
      return (
        Math.abs(xCenterConverted - ovr.xCoordinate) <= CENTER_TOLERANCE &&
        Math.abs(yCenterConverted - ovr.yCoordinate) <= CENTER_TOLERANCE
      )
    })

    if (match) {
      return {
        nominalThickness: GeckoMeasurement.CreateFromValue(
          match.nominalValue,
          match.nominalUnit ?? GeckoMeasurementUnit.Millimeters
        ),
        installationDate: undefined,
      }
    }
  }

  const component = assetModel.data.components.get(targetComponentId)
  if (!component) {
    throw new Error(
      `getManufactureSpecsForBinFn was passed a targetComponentId (${targetComponentId}) that's not in the provided assetModel`
    )
  }
  switch (component.type) {
    // TANK COMPONENTS
    case GeckoComponentModelType.VerticalTankShell: {
      return getManufactureSpecsForVerticalTankShell({
        binBoundsToGetSpecsFor,
        tankShellInstallDate: component.installationDate,
        plates: component.platesArray,
        shellCourses: component.coursesArray,
        baseInstallDate: assetModel.data.installation_date,
        latestDateToUse,
      })
    }
    case GeckoComponentModelType.HorizontalTankShell: {
      return getManufactureSpecsForHorizontalTankShell({
        binBoundsToGetSpecsFor,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.wallNominalThickness,
      })
    }
    case GeckoComponentModelType.TankFloor: {
      return getManufactureSpecsForTankFloor({
        binBoundsToGetSpecsFor,
        plates: component.platesArray,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.baseNominalThickness,
        floorRadius: component.radius,
        annularRings: component.annularRingsArray,
        latestDateToUse,
      })
    }
    case GeckoComponentModelType.TankFlatRoof: {
      return getManufactureSpecsForTankFlatRoof({
        binBoundsToGetSpecsFor,
        plates: component.platesArray,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.baseNominalThickness,
        latestDateToUse,
      })
    }
    // BOILERS
    case GeckoComponentModelType.LegacyBoilerWalls: {
      return getManufactureSpecsForLegacyBoilerWalls({
        binBoundsToGetSpecsFor,
        legacyBoilerWallsInstallDate: component.installationDate,
        allRepairs: component.repairRegionsArray,
        baseInstallDate: assetModel.data.installation_date,
        walls: component.wallsArray,
        latestDateToUse,
      })
    }
    case GeckoComponentModelType.LegacyCone: {
      return getManufactureSpecsForCone({
        courses: component.coursesArray,
        baseInstallDate: assetModel.data.installation_date,
        legacyConeInstallDate: component.installationDate,
        binBoundsToGetSpecsFor,
      })
    }
    case GeckoComponentModelType.Cone: {
      return getManufactureSpecsForCone({
        courses: component.coursesArray,
        baseInstallDate: assetModel.data.installation_date,
        legacyConeInstallDate: component.installationDate,
        binBoundsToGetSpecsFor,
      })
    }
    // PIPES
    case GeckoComponentModelType.PipeSection: {
      return getManufactureSpecsForPipe({
        binBoundsToGetSpecsFor,
        pipeSectionInstallDate: component.installationDate,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.wallNominalThickness,
      })
    }
    // SHIPS
    case GeckoComponentModelType.ShipDeck: {
      return getManufactureSpecsForShipDeck({
        binBoundsToGetSpecsFor,
        polygonalPlates: component.polygonalPlatesArray,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.baseNominalThickness,
        latestDateToUse,
        bins: [binBoundsToGetSpecsFor],
      })
    }

    case GeckoComponentModelType.BasicShipHull: {
      return getManufactureSpecsForShipHull({
        binBoundsToGetSpecsFor,
        component,
        baseInstallDate: assetModel.data.installation_date,
        latestDateToUse,
      })
    }

    case GeckoComponentModelType.LaunchTube: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }

    case GeckoComponentModelType.Dome: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }

    case GeckoComponentModelType.DomeWall: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }

    case GeckoComponentModelType.FlatCircularFloor: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }
    case GeckoComponentModelType.PipeElbow: {
      return {
        nominalThickness: component.wallNominalThickness,
        installationDate: component.installationDate,
      }
    }
    case GeckoComponentModelType.PipeTJoint: {
      return {
        nominalThickness: component.wallNominalThickness,
        installationDate: component.installationDate,
      }
    }
    case GeckoComponentModelType.PipeEccentricReducer: {
      return {
        nominalThickness: component.wallNominalThickness,
        installationDate: component.installationDate,
      }
    }
    case GeckoComponentModelType.VerticalTankFoundation: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }
    case GeckoComponentModelType.MachinedForgingSimple: {
      return {
        nominalThickness: undefined,
        installationDate: undefined,
      }
    }
    case GeckoComponentModelType.PressureVesselShell: {
      return getManufactureSpecsForPressureVesselComponents({
        binBoundsToGetSpecsFor,
        componentInstallDate: component.installationDate,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.wallNominalThickness,
      })
    }
    case GeckoComponentModelType.HemisphericalHead: {
      return getManufactureSpecsForPressureVesselComponents({
        binBoundsToGetSpecsFor,
        componentInstallDate: component.installationDate,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.wallNominalThickness,
      })
    }
    case GeckoComponentModelType.TorisphericalHead: {
      return getManufactureSpecsForPressureVesselComponents({
        binBoundsToGetSpecsFor,
        componentInstallDate: component.installationDate,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.wallNominalThickness,
      })
    }
    case GeckoComponentModelType.EllipsoidalHead: {
      return getManufactureSpecsForPressureVesselComponents({
        binBoundsToGetSpecsFor,
        componentInstallDate: component.installationDate,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.wallNominalThickness,
      })
    }
    case GeckoComponentModelType.SphereFlatCap: {
      return getManufactureSpecsForPressureVesselComponents({
        binBoundsToGetSpecsFor,
        componentInstallDate: component.installationDate,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.wallNominalThickness,
      })
    }
    case GeckoComponentModelType.HoodStraight: {
      return getManufactureSpecsForHood({
        binBoundsToGetSpecsFor,
        componentInstall: component.installationDate,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.wallNominalThickness,
      })
    }
    case GeckoComponentModelType.HoodBend: {
      return getManufactureSpecsForHood({
        binBoundsToGetSpecsFor,
        componentInstall: component.installationDate,
        baseInstallDate: assetModel.data.installation_date,
        baseNominalThickness: component.wallNominalThickness,
      })
    }
  }
}

//-----------------------------------------------------------------------------

const getLatestDate = (...dates: (Date | undefined)[]): Date | undefined => {
  return dates
    .filter((date): date is Date => date !== undefined)
    .sort((a, b) => b.getTime() - a.getTime())[0]
}

// TANK COMPONENTS
//- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// SHELLS
function getManufactureSpecsForVerticalTankShell(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  tankShellInstallDate: Date | undefined
  plates: GeckoPlate[]
  shellCourses: GeckoShellCourse[]
  baseInstallDate: Date | undefined
  latestDateToUse: Date | undefined
}) {
  const {
    binBoundsToGetSpecsFor,
    tankShellInstallDate,
    plates,
    shellCourses,
    baseInstallDate,
    latestDateToUse,
  } = props
  const mostRecentPlate = getMostRecentPlateInBin(
    plates,
    binBoundsToGetSpecsFor,
    latestDateToUse
  )

  const mostRecentInstallationDate = getLatestDate(
    baseInstallDate,
    mostRecentPlate?.installationDate,
    tankShellInstallDate
  )

  if (
    !mostRecentPlate?.installationDate ||
    mostRecentPlate.installationDate === baseInstallDate
  ) {
    // Assume that any plate with no installation date has the same as the base installation date
    //    (i.e. that it was part of the original construction)
    // With that, if we're not on a repair plate get the course that the top of the bin overlaps with
    //  because that is the shell-course we believe the lowest thickness reading in a bin will be in
    const inCourse = shellCourses.findLast((course) =>
      GeckoComponentRect.Overlaps(course.geometry, binBoundsToGetSpecsFor)
    )
    return {
      nominalThickness:
        inCourse?.wallNominalThickness ?? mostRecentPlate?.wallNominalThickness,
      installationDate: mostRecentInstallationDate,
    }
  }
  return {
    nominalThickness: mostRecentPlate?.wallNominalThickness,
    installationDate: mostRecentInstallationDate,
  }
}
function getManufactureSpecsForHorizontalTankShell(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  baseInstallDate: Date | undefined
  baseNominalThickness: GeckoMeasurement | undefined
}) {
  const { baseInstallDate, baseNominalThickness } = props
  return {
    nominalThickness: baseNominalThickness,
    installationDate: baseInstallDate,
  }
}

//- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// FLOOR

export function calculateBinDistanceFromFloorCenter(
  binBounds: GeckoComponentRect,
  floorRadius: GeckoMeasurement | undefined,
  unit: GeckoMeasurementUnit
): [number, number, number, number, number] | undefined {
  // assume the origin is (0,0), so the radius is both the center x and y of the floor
  //logic here is kinda funky - since we wanna err on the side of every bin even intersecting slightly being a part of the annular
  // we need to check every corner and center of the bin to see if it is within the annular ring

  if (!floorRadius) {
    return undefined
  }

  const binStartX = binBounds.x.start.convertTo(unit)
  const binEndX = binBounds.x.end.convertTo(unit)
  const binStartY = binBounds.y.start.convertTo(unit)
  const binEndY = binBounds.y.end.convertTo(unit)
  const floorRadiusConsistentUnit = floorRadius.convertTo(unit)

  const centerOfBinX = (binStartX.num + binEndX.num) / 2
  const centerOfBinY = (binStartY.num + binEndY.num) / 2

  // Floor center is at (floorRadius, floorRadius) based on the comment
  const floorCenterX = floorRadiusConsistentUnit.num
  const floorCenterY = floorRadiusConsistentUnit.num

  const centerDistance = Math.sqrt(
    Math.pow(centerOfBinX - floorCenterX, 2) +
      Math.pow(centerOfBinY - floorCenterY, 2)
  )

  // Distance from each corner to floor center
  const topLeftDistance = Math.sqrt(
    Math.pow(binStartX.num - floorCenterX, 2) +
      Math.pow(binEndY.num - floorCenterY, 2)
  )

  const topRightDistance = Math.sqrt(
    Math.pow(binEndX.num - floorCenterX, 2) +
      Math.pow(binEndY.num - floorCenterY, 2)
  )

  const bottomLeftDistance = Math.sqrt(
    Math.pow(binStartX.num - floorCenterX, 2) +
      Math.pow(binStartY.num - floorCenterY, 2)
  )

  const bottomRightDistance = Math.sqrt(
    Math.pow(binEndX.num - floorCenterX, 2) +
      Math.pow(binStartY.num - floorCenterY, 2)
  )

  return [
    centerDistance,
    topLeftDistance,
    topRightDistance,
    bottomLeftDistance,
    bottomRightDistance,
  ]
}

export function findAnnularRing(
  binBounds: GeckoComponentRect,
  floorRadius: GeckoMeasurement | undefined,
  annularRings: GeckoAnnularRing[] | undefined
): GeckoAnnularRing | undefined {
  if (!floorRadius || isNil(annularRings) || annularRings.length === 0) {
    return undefined
  }
  const centerOfBinX = binBounds.x.start.plus(binBounds.x.delta.scale(0.5))

  const distancesFromCenter = calculateBinDistanceFromFloorCenter(
    binBounds,
    floorRadius,
    centerOfBinX.unit
  )

  return annularRings.find((ring) => {
    return distancesFromCenter?.some(
      (distance) =>
        distance >= ring.innerRadius.convertTo(centerOfBinX.unit).num &&
        distance <= ring.outerRadius.convertTo(centerOfBinX.unit).num
    )
  })
}
function getManufactureSpecsForTankFloor(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  plates: GeckoPlate[]
  baseInstallDate: Date | undefined
  baseNominalThickness: GeckoMeasurement | undefined
  floorRadius: GeckoMeasurement | undefined
  annularRings: GeckoAnnularRing[] | undefined
  latestDateToUse: Date | undefined
}) {
  const {
    binBoundsToGetSpecsFor,
    plates,
    baseInstallDate,
    baseNominalThickness,
    floorRadius,
    annularRings,
    latestDateToUse,
  } = props

  // Annular rings supersede the plates (and welds) drawn beneath them, so bins in the
  // annular ring region use the ring's nominal. Rings with a non-positive inner radius
  // (not yet positioned) would claim the whole floor, so they are ignored, and a ring
  // without a nominal falls through to the plate logic below.
  const positionedAnnularRings = annularRings?.filter(
    (ring) => ring.innerRadius.convertTo(GeckoMeasurementUnit.Meters).num > 0
  )
  const selectedAnnularRing = findAnnularRing(
    binBoundsToGetSpecsFor,
    floorRadius,
    positionedAnnularRings
  )
  if (selectedAnnularRing?.baseNominalThickness !== undefined) {
    return {
      nominalThickness: selectedAnnularRing.baseNominalThickness,
      installationDate: baseInstallDate,
    }
  }

  const mostRecentPlate = getMostRecentPlateInBin(
    plates,
    binBoundsToGetSpecsFor,
    latestDateToUse
  )
  if (
    !mostRecentPlate?.installationDate ||
    mostRecentPlate.installationDate === baseInstallDate
  ) {
    // Assume that any plate with no installation date has the same as the base installation date
    //    (i.e. that it was part of the original construction)
    return {
      nominalThickness:
        mostRecentPlate?.wallNominalThickness ?? baseNominalThickness,
      installationDate: baseInstallDate,
    }
  }
  return {
    nominalThickness: mostRecentPlate.wallNominalThickness,
    installationDate: mostRecentPlate.installationDate,
  }
}
//- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// ROOF
function getManufactureSpecsForTankFlatRoof(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  plates: GeckoPlate[]
  baseInstallDate: Date | undefined
  baseNominalThickness: GeckoMeasurement | undefined
  latestDateToUse: Date | undefined
}) {
  const {
    binBoundsToGetSpecsFor,
    plates,
    baseInstallDate,
    baseNominalThickness,
    latestDateToUse,
  } = props
  const mostRecentPlate = getMostRecentPlateInBin(
    plates,
    binBoundsToGetSpecsFor,
    latestDateToUse
  )
  if (
    !mostRecentPlate?.installationDate ||
    mostRecentPlate.installationDate === baseInstallDate
  ) {
    // Assume that any plate with no installation date has the same as the base installation date
    //    (i.e. that it was part of the original construction)
    return {
      nominalThickness:
        mostRecentPlate?.wallNominalThickness ?? baseNominalThickness,
      installationDate: baseInstallDate,
    }
  }
  return {
    nominalThickness: mostRecentPlate?.wallNominalThickness,
    installationDate: mostRecentPlate?.installationDate,
  }
}
//- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Cone
function getManufactureSpecsForCone(props: {
  courses: Array<GeckoConeCourse>
  baseInstallDate: Date | undefined
  legacyConeInstallDate: Date | undefined
  binBoundsToGetSpecsFor: GeckoComponentRect
}) {
  const {
    courses,
    baseInstallDate,
    legacyConeInstallDate,
    binBoundsToGetSpecsFor,
  } = props
  // Assume that any plate with no installation date has the same as the base installation date
  //    (i.e. that it was part of the original construction)
  // With that, if we're not on a repair plate get the course that the top of the bin overlaps with
  //  because that is the shell-course we believe the lowest thickness reading in a bin will be in
  const inCourse = courses.find((c) =>
    GeckoComponentRect.Contains(c.geometry, binBoundsToGetSpecsFor.end)
  )
  const mostRecentInstallationDate = getLatestDate(
    baseInstallDate,
    legacyConeInstallDate
  )
  return {
    nominalThickness: inCourse?.wallNominalThickness,
    installationDate: mostRecentInstallationDate,
  }
}
//-----------------------------------------------------------------------------
// BOILER COMPONENTS
function getManufactureSpecsForLegacyBoilerWalls(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  legacyBoilerWallsInstallDate: Date | undefined
  allRepairs: GeckoLegacyBoilerRepairRegion[]
  walls: GeckoLegacyBoilerWall[]
  baseInstallDate: Date | undefined
  latestDateToUse: Date | undefined
}) {
  const {
    binBoundsToGetSpecsFor,
    legacyBoilerWallsInstallDate,
    allRepairs,
    baseInstallDate,
    walls,
    latestDateToUse,
  } = props
  const mostRecentRepair = allRepairs
    .filter((repair) => {
      return (
        (isNil(latestDateToUse)
          ? true
          : isNil(repair.installationDate)
            ? true
            : repair.installationDate <= latestDateToUse) &&
        GeckoComponentRect.Overlaps(repair.geometry, binBoundsToGetSpecsFor)
      )
    })
    // Sort by installation date (newest), with ties broken by wall thickness (thinnest)
    .toSorted((a, b) => {
      // prioritize the thinnest plates
      if (!a.wallNominalThickness && !b.wallNominalThickness) return 0
      if (!a.wallNominalThickness) return 1
      if (!b.wallNominalThickness) return -1
      return a.wallNominalThickness.lt(b.wallNominalThickness) ? -1 : 1
    })
    .toSorted((a, b) => {
      // prioritize most recent installation
      if (!a.installationDate && !b.installationDate) return 0
      if (!a.installationDate) return 1
      if (!b.installationDate) return -1
      return a.installationDate > b.installationDate ? -1 : 1
    })
    .at(0)
  const mostRecentInstallationDate = getLatestDate(
    baseInstallDate,
    mostRecentRepair?.installationDate,
    legacyBoilerWallsInstallDate
  )

  if (
    !mostRecentRepair?.installationDate ||
    mostRecentRepair.installationDate === baseInstallDate
  ) {
    // Assume that any plate with no installation date has the same as the base installation date
    //    (i.e. that it was part of the original construction)
    const wallCoordsAreOn = walls.find((wall) =>
      GeckoComponentRect.Overlaps(wall.geometry, binBoundsToGetSpecsFor)
    )
    return {
      nominalThickness: wallCoordsAreOn?.wallNominalThickness,
      installationDate: mostRecentInstallationDate,
    }
  }
  return {
    nominalThickness: mostRecentRepair?.wallNominalThickness,
    installationDate: mostRecentInstallationDate,
  }
}
//-----------------------------------------------------------------------------
// PIPES
function getManufactureSpecsForPipe(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  pipeSectionInstallDate: Date | undefined
  baseInstallDate: Date | undefined
  baseNominalThickness: GeckoMeasurement | undefined
}) {
  const { baseInstallDate, baseNominalThickness, pipeSectionInstallDate } =
    props
  const mostRecentInstallationDate = getLatestDate(
    baseInstallDate,
    pipeSectionInstallDate
  )
  return {
    nominalThickness: baseNominalThickness,
    installationDate: mostRecentInstallationDate,
  }
}

//-----------------------------------------------------------------------------
// HOODS
function getManufactureSpecsForHood(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  componentInstall: Date | undefined
  baseInstallDate: Date | undefined
  baseNominalThickness: GeckoMeasurement | undefined
}) {
  const { baseInstallDate, baseNominalThickness, componentInstall } = props
  const mostRecentInstallationDate = getLatestDate(
    baseInstallDate,
    componentInstall
  )
  return {
    nominalThickness: baseNominalThickness,
    installationDate: mostRecentInstallationDate,
  }
}

//-----------------------------------------------------------------------------
// SHIPS
function getManufactureSpecsForShipDeck(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  polygonalPlates: GeckoPolygonalPlate[]
  baseInstallDate: Date | undefined
  baseNominalThickness: GeckoMeasurement | undefined
  latestDateToUse: Date | undefined
  bins?: ReadonlyArray<GeckoComponentRect>
}) {
  const {
    binBoundsToGetSpecsFor,
    polygonalPlates,
    baseInstallDate,
    baseNominalThickness,
    latestDateToUse,
  } = props

  const mostRecentPlate = getMostRecentPolygonalPlateInBin(
    polygonalPlates,
    binBoundsToGetSpecsFor,
    latestDateToUse
  )

  if (
    !mostRecentPlate?.installationDate ||
    mostRecentPlate.installationDate === baseInstallDate
  ) {
    // Assume that any plate with no installation date has the same as the base installation date
    //    (i.e. that it was part of the original construction)
    return {
      nominalThickness:
        mostRecentPlate?.wallNominalThickness ?? baseNominalThickness,
      installationDate: baseInstallDate,
    }
  }
  return {
    nominalThickness:
      mostRecentPlate?.wallNominalThickness ?? baseNominalThickness,
    installationDate: mostRecentPlate?.installationDate,
  }
}

// Weak keys avoid retaining obsolete asset models while amortizing plate validation and spatial indexing across bins.
const basicShipHullNominalResolverCache = new WeakMap<
  GeckoShipHull,
  ReturnType<typeof createBasicShipHullNominalResolver>
>()

function getManufactureSpecsForShipHull(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  component: GeckoShipHull
  baseInstallDate: Date | undefined
  latestDateToUse: Date | undefined
}) {
  const {
    binBoundsToGetSpecsFor,
    component,
    baseInstallDate,
    latestDateToUse,
  } = props

  const hasPolygonalNominal = component.polygonalPlatesArray.some(
    (plate) => !isNil(plate.wallNominalThickness)
  )
  if (hasPolygonalNominal) {
    let resolver = basicShipHullNominalResolverCache.get(component)
    if (!resolver) {
      resolver = createBasicShipHullNominalResolver({
        componentId: binBoundsToGetSpecsFor.componentID,
        polygonalPlates: component.polygonalPlatesArray,
        rectangularPlates: component.platesArray,
        baseInstallationDate: baseInstallDate,
      })
      basicShipHullNominalResolverCache.set(component, resolver)
    }
    return resolver(binBoundsToGetSpecsFor, latestDateToUse)
  }

  const mostRecentPlate = getMostRecentPlateInBin(
    component.platesArray,
    binBoundsToGetSpecsFor,
    latestDateToUse
  )

  if (
    !mostRecentPlate?.installationDate ||
    mostRecentPlate.installationDate === baseInstallDate
  ) {
    // Assume that any plate with no installation date has the same as the base installation date
    //    (i.e. that it was part of the original construction)

    return {
      nominalThickness: mostRecentPlate?.wallNominalThickness,
      installationDate: baseInstallDate,
    }
  }

  return {
    nominalThickness: mostRecentPlate?.wallNominalThickness,
    installationDate: mostRecentPlate?.installationDate,
  }
}

// ----------------------------------------------------------------------------------------------------------------

// Pressure Vessels
function getManufactureSpecsForPressureVesselComponents(props: {
  binBoundsToGetSpecsFor: GeckoComponentRect
  componentInstallDate: Date | undefined
  baseInstallDate: Date | undefined
  baseNominalThickness: GeckoMeasurement | undefined
}) {
  const { componentInstallDate, baseInstallDate, baseNominalThickness } = props

  const mostRecentInstallationDate = getLatestDate(
    baseInstallDate,
    componentInstallDate
  )

  return {
    nominalThickness: baseNominalThickness,
    installationDate: mostRecentInstallationDate,
  }
}
// ----------------------------------------------------------------------------------------------------------------

/**
 * A function to get the radius from a `GeckoComponentModel`, if applicable
 */
export function getComponentRadius(
  componentRam: GeckoComponentModel | undefined
): GeckoMeasurement | undefined {
  if (!componentRam) return undefined
  switch (componentRam.type) {
    case GeckoComponentModelType.VerticalTankShell:
    case GeckoComponentModelType.HorizontalTankShell:
    case GeckoComponentModelType.TankFloor:
    case GeckoComponentModelType.TankFlatRoof:
    case GeckoComponentModelType.PipeSection:
    case GeckoComponentModelType.PressureVesselShell:
    case GeckoComponentModelType.HemisphericalHead:
    case GeckoComponentModelType.EllipsoidalHead:
    case GeckoComponentModelType.SphereFlatCap:
    case GeckoComponentModelType.LaunchTube:
      return componentRam.radius
    case GeckoComponentModelType.LegacyCone: {
      return componentRam.outerRadius
    }
    case GeckoComponentModelType.ShipDeck: {
      return undefined
    }
    case GeckoComponentModelType.LegacyBoilerWalls: {
      return undefined
    }
  }
}
// ----------------------------------------------------------------------------------------------------------------
/**
 * A function to get the height from a `GeckoComponentModel`, if applicable
 */
export function getComponentHeight(
  componentRam: GeckoComponentModel | undefined
): GeckoMeasurement | undefined {
  if (!componentRam) return undefined
  switch (componentRam.type) {
    case GeckoComponentModelType.VerticalTankShell:
    case GeckoComponentModelType.LaunchTube:
    case GeckoComponentModelType.LegacyBoilerWalls:
      return componentRam.height
    case GeckoComponentModelType.PressureVesselShell:
      return undefined
    case GeckoComponentModelType.HorizontalTankShell: {
      return undefined
    }
    case GeckoComponentModelType.TankFloor: {
      return undefined
    }
    case GeckoComponentModelType.TankFlatRoof: {
      return undefined
    }
    case GeckoComponentModelType.PipeSection: {
      return undefined
    }
    case GeckoComponentModelType.LegacyCone: {
      return undefined
    }
    case GeckoComponentModelType.ShipDeck: {
      return undefined
    }
  }
}
